import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'

// ── Payload types ─────────────────────────────────────────────────────────────

export type ToastVariant = 'success' | 'warn' | 'error' | 'info'

export interface ToastPayload {
  kind: 'toast'
  variant: ToastVariant
  text: string
}

export interface ModalPayload {
  kind: 'modal'
  title: string
  children: ReactNode
}

export interface DrawerPayload {
  kind: 'drawer'
  title: string
  children: ReactNode
}

export type OverlayPayload = ToastPayload | ModalPayload | DrawerPayload

// ── Stack entry ───────────────────────────────────────────────────────────────

export interface OverlayEntry {
  id: string
  onClose: () => void
  payload: OverlayPayload
}

// ── Context ───────────────────────────────────────────────────────────────────

export interface OverlayContextValue {
  /** Current overlay stack — topmost is last element. */
  stack: OverlayEntry[]
  push: (entry: OverlayEntry) => void
  pop: (id: string) => void
  /**
   * Returns the onClose handler of the topmost overlay, or null if empty.
   *
   * Exposed for commit 5 (useBackButton hook). AppShell will call this inside
   * useBackButton to wire Telegram BackButton LIFO behaviour.
   * TODO: commit 5 — integrate via useBackButton(() => getTopOverlayClose(), [stack])
   */
  getTopOverlayClose: () => (() => void) | null
}

export const OverlayContext = createContext<OverlayContextValue | null>(null)

function useOverlayContext(): OverlayContextValue {
  const ctx = useContext(OverlayContext)
  if (ctx === null) {
    throw new Error('useOverlay hooks must be used inside <OverlayProvider>')
  }
  return ctx
}

// ── Provider state (consumed by OverlayRoot) ──────────────────────────────────

let _seq = 0
function nextId(): string {
  _seq += 1
  return `ov-${_seq}`
}

export function useOverlayState(): OverlayContextValue {
  const [stack, setStack] = useState<OverlayEntry[]>([])

  // Scroll-lock: add overflow:hidden to body when any overlay is open
  useEffect(() => {
    const hasOverlay = stack.length > 0
    const prev = document.body.style.overflow
    if (hasOverlay) {
      document.body.style.overflow = 'hidden'
    }
    return () => {
      if (hasOverlay) {
        document.body.style.overflow = prev
      }
    }
  }, [stack.length])

  const push = useCallback((entry: OverlayEntry): void => {
    setStack((prev) => [...prev, entry])
  }, [])

  const pop = useCallback((id: string): void => {
    setStack((prev) => prev.filter((e) => e.id !== id))
  }, [])

  const getTopOverlayClose = useCallback((): (() => void) | null => {
    const top = stack[stack.length - 1]
    return top !== undefined ? top.onClose : null
  }, [stack])

  return { stack, push, pop, getTopOverlayClose }
}

// ── Public hooks ──────────────────────────────────────────────────────────────

export interface ToastAPI {
  showToast: (variant: ToastVariant, text: string) => void
}

export function useToast(): ToastAPI {
  const { push, pop } = useOverlayContext()

  const showToast = useCallback(
    (variant: ToastVariant, text: string): void => {
      const id = nextId()
      const onClose = (): void => pop(id)
      const payload: ToastPayload = { kind: 'toast', variant, text }
      push({ id, onClose, payload })
    },
    [push, pop],
  )

  return { showToast }
}

export interface ModalOptions {
  title: string
  children: ReactNode
  onClose?: () => void
}

export interface ModalAPI {
  openModal: (opts: ModalOptions) => () => void
}

export function useModal(): ModalAPI {
  const { push, pop } = useOverlayContext()

  const openModal = useCallback(
    (opts: ModalOptions): (() => void) => {
      const id = nextId()
      const onClose = (): void => {
        pop(id)
        opts.onClose?.()
      }
      const payload: ModalPayload = {
        kind: 'modal',
        title: opts.title,
        children: opts.children,
      }
      push({ id, onClose, payload })
      return onClose
    },
    [push, pop],
  )

  return { openModal }
}

export interface DrawerOptions {
  title: string
  children: ReactNode
  onClose?: () => void
}

export interface DrawerAPI {
  openDrawer: (opts: DrawerOptions) => () => void
}

export function useDrawer(): DrawerAPI {
  const { push, pop } = useOverlayContext()

  const openDrawer = useCallback(
    (opts: DrawerOptions): (() => void) => {
      const id = nextId()
      const onClose = (): void => {
        pop(id)
        opts.onClose?.()
      }
      const payload: DrawerPayload = {
        kind: 'drawer',
        title: opts.title,
        children: opts.children,
      }
      push({ id, onClose, payload })
      return onClose
    },
    [push, pop],
  )

  return { openDrawer }
}
